import { Component, ComponentRef, EventEmitter, Inject, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';;
import { CategoryType } from '@core/models/category-type.model';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractNatioNego } from '@core/models/contract-natio-nego.model';
import { Contract } from '@core/models/contract.model';
import { FilteredNegociationType, NegotiationType } from '@core/models/negotiation-type.model';
import { CategoryTypeService } from '@core/services/category-type/category-type.service';
import { ContractCgvModelUpdateService } from '@core/services/contract-cgv-model-update/contract-cgv-model-update.service';
import { ContractNatioNegoService } from '@core/services/contract-natio-nego/contract-natio-nego.service';
import { NegotiationTypeService } from '@core/services/negotiation-type/negotiation-type.service';
import { NatioNegoConditionDynamicComponent } from '@shared/contract/contract-item/contract-natio-nego/natio-nego-condition-dynamic/natio-nego-condition-dynamic.component';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { finalize, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

type ConditionSummaryRow = Partial<Record<string, string>>;

@Component({
standalone: false,
  selector: 'app-natio-nego-managing-dialog',
  templateUrl: './natio-nego-managing-dialog.component.html',
  styleUrls: ['./natio-nego-managing-dialog.component.scss'],
})
export class NatioNegoManagingDialogComponent implements OnInit {
  @Output() saveStarted = new EventEmitter<void>();
  @Output() saveFinished = new EventEmitter<void>();

  // Load & Display
  isCategoryTypeLoading: boolean = true;
  isNegotiationTypeLoading: boolean = true;
  categoryTypeList: CategoryType[] = [];
  negotiationTypeList: NegotiationType[] = [];
  filteredNegotiationTypeList: Observable<NegotiationType[]>;
  isSaving = false;
  private isDialogInitializing = true;
  private initializationTimer?: ReturnType<typeof setTimeout>;

  get isLoading(): boolean {
    return this.isCategoryTypeLoading || this.isNegotiationTypeLoading || this.isDialogInitializing;
  }
  firstDayOfYear = moment().startOf('year');
  lastDayOfYear = moment().endOf('year');

  // Rate Input
  readonly displayViews: { id: number; display: string }[] = [
    { id: 0, display: '%' },
    { id: 1, display: '€' },
  ];

  public selectedView = this.displayViews[0].id;

  // Display Conditions
  @ViewChild('dynamicConditionComponent', { read: ViewContainerRef, static: false })
  container!: ViewContainerRef;
  conditionComponentList: ComponentRef<NatioNegoConditionDynamicComponent>[] = [];
  readonly conditionSummaryColumns = [
    { id: 7, label: 'Cible' },
    { id: 1, label: 'Annonceur' },
    { id: 'number-1', label: 'N° annonceur' },
    { id: 2, label: 'Mandataire / Agence' },
    { id: 'number-2', label: 'N° mand./ag.' },
    { id: 4, label: 'Produit' },
    { id: 'number-4', label: 'N° produit' },
    { id: 3, label: 'Secodip' },
    { id: 6, label: 'Campagne' },
  ];
  readonly conditionSummaryColumnIds = this.conditionSummaryColumns.map(column => `category-${column.id}`);
  conditionSummaryRows: ConditionSummaryRow[] = [];

  negotiationForm: FormGroup;
  negotiationTypeFormControl = new FormControl(null, [Validators.required]);
  rateFormControl = new FormControl(null, [Validators.required, this.maxRateValidator(999)]);

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private categoryTypeService: CategoryTypeService,
    private negotiationTypeService: NegotiationTypeService,
    private contractNatioNegoService: ContractNatioNegoService,
    private contractCgvModelUpdateService: ContractCgvModelUpdateService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<NatioNegoManagingDialogComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contract: Contract;
      contractItem: ContractItem;
      negotiationData: ContractNatioNego;
      isDuplication: boolean;
    }
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getCategoryTypeList();
    this.getNegotiationTypeList();
  }

  ngOnDestroy(): void {
    clearTimeout(this.initializationTimer);
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  private initForm() {
    this.firstDayOfYear = moment().year(Number(this.data.contractItem.year)).startOf('year');
    this.lastDayOfYear = moment().year(Number(this.data.contractItem.year)).endOf('year');

    this.negotiationForm = this.fb.group({
      startDate: [this.firstDayOfYear || null, [Validators.required]],
      endDate: [this.lastDayOfYear || null, [Validators.required]],
    });
  }

  // Validation personnalisée pour la valeur maximale
  maxRateValidator(max: number) {
    return (control: FormControl) => {
      const value = control.value;
      if (this.selectedView === 0 && value && (value < -max || value > max)) {
        return { maxRateExceeded: true }; // Retourne une erreur si la min < valeur < max && view = %
      }
      return null; // Pas d'erreur si la validation est réussie
    };
  }

  // Re-Open negotiation
  private initDialog() {
    if (this.data.negotiationData && !this.isNegotiationTypeLoading && this.negotiationTypeList.length > 0) {
      // NegotiationType
      const negotiationType = this.negotiationTypeList.find(item => item.id === this.data.negotiationData.negotiationType.id);

      if (negotiationType) {
        this.negotiationTypeFormControl.setValue(
          this.displayNegotiationType(new FilteredNegociationType({ id: negotiationType.id, name: negotiationType.name }))
        );
      }

      // Date
      this.negotiationForm.get('startDate').setValue(this.data.negotiationData.startDate);
      this.negotiationForm.get('endDate').setValue(this.data.negotiationData.endDate);

      // Rate
      if (this.data.negotiationData.rate) {
        this.rateFormControl.setValue(this.data.negotiationData.rate);
        this.selectedView = 0;
      } else {
        this.rateFormControl.setValue(this.data.negotiationData.value);
        this.selectedView = 1;
      }

      // Conditions
      this.data.negotiationData.groupedConditions.map(group => {
        const label = group[0].isInclude ? 'Inclus' : 'Exclus';
        this.createDynamicConditionComponent(group[0].isInclude, label, group);
      });
    }
  }

  public getNegotiationTypeList() {
    this.isNegotiationTypeLoading = true;

    this.negotiationTypeService
      .getNegotiationTypeList({ year: this.firstDayOfYear.format('YYYY') })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        negotiationTypeList => {
          this.isNegotiationTypeLoading = false;
          this.negotiationTypeList = negotiationTypeList;

          this.initializeDialogWhenLoaded();
        },
        error => {
          this.isNegotiationTypeLoading = false;
          this.initializeDialogWhenLoaded();
        }
      );
  }

  private initializeDialogWhenLoaded(): void {
    if (this.isCategoryTypeLoading || this.isNegotiationTypeLoading || this.initializationTimer !== undefined) return;

    this.initializationTimer = setTimeout(() => {
      this.initDialog();
      this.initFilteredNegotiationTypeList();
      this.isDialogInitializing = false;
    }, 700);
  }

  private initFilteredNegotiationTypeList() {
    if (!this.isNegotiationTypeLoading) {
      this.filteredNegotiationTypeList = this.negotiationTypeFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filter(label) : this.negotiationTypeList.slice()))
      );
    }
  }

  /**
   *  Fonction pour gérer les cas de sélection d'option ou de chaîne tapée
   * @param value
   * @private
   */
  private _getLabel(value: string | NegotiationType): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  /***
   * Fonction pour afficher le label dans l'input lors de la sélection
   * @param value
   * @private
   */
  private _filter(value: string = ''): NegotiationType[] {
    const filterValue = value.toLowerCase();
    return this.negotiationTypeList.filter(
      negotiationType => negotiationType.id.toLowerCase().includes(filterValue) || negotiationType.name.toLowerCase().includes(filterValue)
    );
  }

  public displayNegotiationType(negotiationType) {
    return negotiationType?.name && negotiationType?.id ? `${negotiationType.name} (${negotiationType.id})` : negotiationType;
  }

  // Rate

  selectedDisplayView(view): void {
    this.selectedView = view.id;
  }

  // Conditions

  get isAddConditionDisabled(): boolean {
    return this.isLoading || this.conditionComponentList.some(component => component.instance.chosenItemList.length === 0);
  }

  get hasMultipleIncludeGroups(): boolean {
    return this.conditionComponentList.filter(component => component.instance.conditionType?.value === 1).length > 1;
  }

  get hasAvailableConditionCategories(): boolean {
    const lastGroup = this.conditionComponentList[this.conditionComponentList.length - 1]?.instance;
    if (lastGroup && lastGroup.categoryTypeList.length <= 1) return false;

    const usedCategoryIds = new Set<number>();
    this.conditionComponentList.forEach(component => {
      component.instance.selectedCategoryIds.forEach(id => usedCategoryIds.add(id));
    });

    return this.categoryTypeList.some(category => !usedCategoryIds.has(category.id));
  }

  public getCategoryTypeList() {
    this.isCategoryTypeLoading = true;

    this.categoryTypeService
      .getCategoryTypeList({})
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        categoryType => {
          this.categoryTypeList = categoryType;
          this.isCategoryTypeLoading = false;
          this.initializeDialogWhenLoaded();
        },
        error => {
          this.isCategoryTypeLoading = false;
          this.initializeDialogWhenLoaded();
        }
      );
  }

  createDynamicConditionComponent(conditionTypeValue: number, conditionTypeLabel: string, groupedConditions = null) {
    if (!groupedConditions && (this.isAddConditionDisabled || !this.hasAvailableConditionCategories)) return;

    if (this.container) {
      const conditionComponent = this.container.createComponent(NatioNegoConditionDynamicComponent);
      conditionComponent.instance.groupId = this.conditionComponentList.length + 1;
      conditionComponent.instance.contract = this.data.contract;
      conditionComponent.instance.conditionType = { value: conditionTypeValue, label: conditionTypeLabel };
      conditionComponent.instance.categoryTypeList = this.categoryTypeList;
      conditionComponent.instance.groupedConditions = groupedConditions; // not null only for edit/duplicate a nego
      conditionComponent.instance.isDuplication = this.data.isDuplication; // not null only for duplicate a nego

      this.conditionComponentList.push(conditionComponent);

      // Souscrire à l'EventEmitter du composant enfant
      conditionComponent.instance.messageEvent.pipe(takeUntil(this.componentDestroyed$)).subscribe(event => {
        if (event === 'remove') {
          this.removeDynamicConditionComponent(conditionComponent);
        }
      });

      conditionComponent.instance.selectedItemsChange
        .pipe(takeUntil(conditionComponent.instance.componentDestroyed$), takeUntil(this.componentDestroyed$))
        .subscribe(() => this.updateConditionCategoryOptions());

      conditionComponent.changeDetectorRef.detectChanges();
      this.updateConditionCategoryOptions();
    }
  }

  removeDynamicConditionComponent(component: ComponentRef<NatioNegoConditionDynamicComponent>) {
    const index = this.conditionComponentList.indexOf(component);

    if (index !== -1) {
      this.conditionComponentList[index].destroy();
      this.conditionComponentList.splice(index, 1);
      this.updateConditionCategoryOptions();
    }
  }

  private updateConditionCategoryOptions(): void {
    const categoriesUsedAbove = new Set<number>();

    this.conditionComponentList.forEach((component, index) => {
      const selectedCategoryIds = component.instance.selectedCategoryIds;
      const hasLowerGroups = index < this.conditionComponentList.length - 1;
      const availableCategories = this.categoryTypeList.filter(category =>
        hasLowerGroups
          ? selectedCategoryIds.has(category.id)
          : !categoriesUsedAbove.has(category.id) || selectedCategoryIds.has(category.id)
      );

      component.instance.groupId = index + 1;
      component.instance.updateCategoryOptions(availableCategories, hasLowerGroups);
      component.changeDetectorRef.markForCheck();
      selectedCategoryIds.forEach(id => categoriesUsedAbove.add(id));
    });

    this.updateConditionSummary();
  }

  private updateConditionSummary(): void {
    const groups = this.conditionComponentList
      .filter(component => component.instance.conditionType?.value === 1)
      .map(component => component.instance.chosenItemList)
      .filter(items => items.length > 0);

    if (groups.length === 0) {
      this.conditionSummaryRows = [];
      return;
    }

    this.conditionSummaryRows = groups.reduce<ConditionSummaryRow[]>(
      (rows, items) =>
        rows.flatMap(row =>
          items.map(item => ({
            ...row,
            [item.itemType.id]: item.item.name || String(item.item.id),
            [`number-${item.itemType.id}`]: String(item.item.id ?? ''),
          }))
        ),
      [{}]
    );
  }

  // Save

  getNegotiationDate() {
    let formattedStartDate = '';
    let formattedEndDate = '';

    if (typeof this.negotiationForm.get('startDate').value !== 'string') {
      formattedStartDate = moment(this.negotiationForm.get('startDate').value).format('YYYY-MM-DD');
      formattedEndDate = moment(this.negotiationForm.get('endDate').value).format('YYYY-MM-DD');
    } else {
      let [day, month, year] = this.negotiationForm.get('startDate').value.split('/');
      formattedStartDate = `${year}-${month}-${day}`;
      [day, month, year] = this.negotiationForm.get('endDate').value.split('/');
      formattedEndDate = `${year}-${month}-${day}`;
    }

    return {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
  }

  getValuesToSave() {
    const negotiationTypeFormControlValue = this.negotiationTypeFormControl.value;

    const negotiationTypeValue =
      typeof negotiationTypeFormControlValue !== 'string'
        ? this.negotiationTypeList.find(item => negotiationTypeFormControlValue?.id.includes(item.id))
        : this.negotiationTypeList.find(item => negotiationTypeFormControlValue === item.name + ' (' + item.id + ')');

    const negotiationDate = this.getNegotiationDate();

    if (
      this.negotiationForm?.valid &&
      this.negotiationTypeFormControl?.valid &&
      this.negotiationForm?.valid &&
      this.rateFormControl?.valid &&
      this.maxRateValidator(999)(this.rateFormControl) == null
    ) {
      return new ContractNatioNego({
        id: (!this.data.isDuplication ? this.data.negotiationData?.id : null) || null,
        contractItemId: this.data.contractItem?.id || null,
        negotiationType: negotiationTypeValue,
        startDate: negotiationDate.startDate || '',
        endDate: negotiationDate.endDate || '',
        rate: this.selectedView === 0 ? this.rateFormControl.value : null,
        value: this.selectedView === 1 ? this.rateFormControl.value : null,
        groupedConditions: this.conditionComponentList
          .map(conditionComponent => conditionComponent.instance.getConditionList())
          .filter(conditions => conditions.length > 0),
      });
    } else {
      this.markAllAsTouched();
      this.displaySnackBar('Veuillez corriger le formulaire.', 'tv-chip-error');
    }
  }

  public markAllAsTouched() {
    this.negotiationForm.markAllAsTouched();
    this.negotiationTypeFormControl.markAllAsTouched();
    this.rateFormControl.markAllAsTouched();
  }

  onValidationButtonClick() {
    if (this.isSaving || this.isLoading) return;

    const negotiationValues = this.getValuesToSave();

    if (negotiationValues) {
      this.isSaving = true;
      const previousDisableClose = this.dialogRef.disableClose;
      this.dialogRef.disableClose = true;
      this.saveStarted.emit();
      let negotiationSaved = false;
      const saveRequest = negotiationValues.id
        ? this.contractNatioNegoService.edit(negotiationValues, negotiationValues.id.toString())
        : this.contractNatioNegoService.create(negotiationValues);

      saveRequest
        .pipe(
          take(1),
          switchMap(() => {
            negotiationSaved = true;
            return this.updateContractCgvModel();
          }),
          finalize(() => {
            this.isSaving = false;
            this.dialogRef.disableClose = previousDisableClose;
            this.saveFinished.emit();
            this.dialogRef.close();
          })
        )
        .subscribe(
          () => {
            this.displaySnackBar('Enregistrement terminé.', 'tv-chip-success');
          },
          error => {
            if (negotiationSaved) {
              this.displaySnackBar(
                "Erreur lors de la sauvegarde dans l'ancien modèle. Veuillez contacter le support.",
                'tv-chip-error'
              );
            } else {
              const message = negotiationValues.id ? 'Erreur lors de la mise à jour.' : 'Erreur lors de la création.';
              this.displaySnackBar(message, 'tv-chip-error');
            }
          }
        );
    }
  }

  public updateContractCgvModel(): Observable<unknown> {
    return this.contractCgvModelUpdateService
      .updateContractCgvModel(this.data.contractItem.id, this.data.contractItem)
      .pipe(take(1));
  }

  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  onCancelUpdateButtonClick() {
    this.snackBar.open("L'ajout / modification a été annulée.", null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: ['tv-chip-warning'],
    });
    this.dialogRef.close();
  }

  enforceNegativeRate(): void {
    const value = this.rateFormControl.value;

    if (this.negotiationTypeFormControl.value?.rank !== 20) return;

    if (value != null && !isNaN(value) && value > 0 && this.selectedView === 0) {
      this.rateFormControl.setValue(-Math.abs(value));
    }
  }
}
